import { motion } from 'framer-motion';
import { useState } from 'react';

const VideoHeroSection = () => {
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const scrollToCranes = () => {
    const element = document.querySelector('#cranes');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        {!isVideoLoaded && (
          <div className="absolute inset-0 bg-primary"></div>
        )}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          onLoadedData={() => setIsVideoLoaded(true)}
          className="w-full h-full object-cover"
        >
          <source
            src="https://videos.pexels.com/video-files/3820873/3820873-hd_1920_1080_25fps.mp4"
            type="video/mp4"
          />
          Your browser does not support the video tag.
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
      </div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-20 left-10 w-32 h-32 bg-accent/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-20 right-10 w-40 h-40 bg-primary/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-5xl mx-auto text-center">
          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-6"
          >
            <motion.h1 
              className="text-6xl md:text-7xl lg:text-8xl font-poppins font-black text-white mb-4 leading-tight"
              animate={{
                textShadow: [
                  "0 0 20px rgba(212, 165, 116, 0.3)",
                  "0 0 40px rgba(212, 165, 116, 0.5)",
                  "0 0 20px rgba(212, 165, 116, 0.3)",
                ]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              POWER IN
              <br />
              <motion.span 
                className="text-accent"
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                EVERY LIFT
              </motion.span>
            </motion.h1>
          </motion.div>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-xl md:text-2xl text-white/90 font-inter mb-8 leading-relaxed max-w-3xl mx-auto"
          >
            Leading crane rental and heavy equipment provider in Saudi Arabia
            <br />
            <motion.strong 
              className="text-accent"
              animate={{
                opacity: [0.8, 1, 0.8],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              SANY • XCMG • Tadano
            </motion.strong>
          </motion.p>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="flex flex-wrap items-center justify-center gap-8 mb-12"
          >
            {[
              { value: '15+', label: 'Years Experience' },
              { value: '300+', label: 'Ton Capacity' },
              { value: '200+', label: 'Projects Completed' }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                whileHover={{ scale: 1.1, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <motion.div 
                  className="text-5xl font-poppins font-black text-accent mb-2"
                  animate={{
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: index * 0.3
                  }}
                >
                  {stat.value}
                </motion.div>
                <div className="text-white/80 font-inter text-sm uppercase tracking-wider">{stat.label}</div>
              </motion.div>
            )).reduce((acc, curr, index) => {
              if (index === 0) return [curr];
              return [...acc, <div key={`divider-${index}`} className="w-px h-12 bg-white/20"></div>, curr];
            }, [] as JSX.Element[])}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-wrap items-center justify-center gap-4"
          >
            <motion.button
              onClick={scrollToCranes}
              className="px-10 py-5 bg-accent text-white rounded-full font-inter font-bold text-base hover:bg-accent-dark transition-all duration-300 shadow-2xl hover:shadow-accent/50 flex items-center gap-3 cursor-pointer whitespace-nowrap"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
              animate={{
                boxShadow: [
                  "0 10px 40px rgba(212, 165, 116, 0.3)",
                  "0 10px 60px rgba(212, 165, 116, 0.5)",
                  "0 10px 40px rgba(212, 165, 116, 0.3)",
                ]
              }}
              transition={{
                boxShadow: {
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
            >
              Explore Our Cranes
              <motion.div 
                className="w-6 h-6 flex items-center justify-center"
                animate={{
                  y: [0, 5, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <i className="ri-arrow-down-line text-xl"></i>
              </motion.div>
            </motion.button>

            <motion.button
              onClick={scrollToContact}
              className="px-10 py-5 bg-white/10 backdrop-blur-sm text-white rounded-full font-inter font-bold text-base border-2 border-white hover:bg-white hover:text-primary transition-all duration-300 flex items-center gap-3 cursor-pointer whitespace-nowrap"
              whileHover={{ scale: 1.05, y: -3 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div 
                className="w-6 h-6 flex items-center justify-center"
                animate={{
                  rotate: [0, 10, -10, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                <i className="ri-phone-line text-xl"></i>
              </motion.div>
              Get Quote
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 15, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2 cursor-pointer"
          whileHover={{ scale: 1.2 }}
        >
          <motion.div 
            className="w-1 h-2 bg-white/50 rounded-full"
            animate={{
              y: [0, 12, 0],
              opacity: [1, 0.3, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          ></motion.div>
        </motion.div>
      </motion.div>

      {/* Floating Particles */}
      {[...Array(5)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 bg-accent/30 rounded-full"
          style={{
            left: `${20 + i * 15}%`,
            top: `${30 + i * 10}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0.2, 0.8, 0.2],
            scale: [1, 1.5, 1],
          }}
          transition={{
            duration: 4 + i,
            repeat: Infinity,
            ease: "easeInOut",
            delay: i * 0.5,
          }}
        />
      ))}
    </section>
  );
};

export default VideoHeroSection;
